import React, { useState, useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { useDatasets } from '../../../hooks/useDatasets';
import { datasetService } from '../../../services/datasetService';
import { useQueryClient } from '@tanstack/react-query';
import { datasetKeys } from '../../../hooks/useDatasets';
import { useAccess } from '../../../hooks/useAccess';
import { toast } from 'sonner';

// Sentinel Select value for "no team" — shadcn Select items can't use "".
const PRIVATE_OWNER = '__private__';

interface DatasetSelectorProps {
  value: string | null;
  onChange: (datasetId: string | null) => void;
  label?: string;
  helpText?: string;
  className?: string;
  autoSelectDefault?: boolean;
}

const DEFAULT_HELP_TEXT =
  'Select an existing Collection or create a new one. Your records will be organized into the selected collection for better management and analysis.';

export const DatasetSelector: React.FC<DatasetSelectorProps> = ({
  value,
  onChange,
  label = 'Collection',
  helpText = DEFAULT_HELP_TEXT,
  className = '',
  autoSelectDefault = true,
}) => {
  const [isCreating, setIsCreating] = useState(false);
  const [newDatasetName, setNewDatasetName] = useState('');
  const [createMode, setCreateMode] = useState(false);
  // D2: owning team for a new collection. PRIVATE_OWNER (default) = private to
  // the creator; a team id = shared with that team.
  const [ownerTeam, setOwnerTeam] = useState<string>(PRIVATE_OWNER);
  const queryClient = useQueryClient();

  // The caller's own teams — populates the owning-team picker (D2).
  const { teams } = useAccess();

  // Fetch all datasets
  const { data: datasetsResponse, isLoading } = useDatasets();
  const datasets = useMemo(() => datasetsResponse?.datasets || [], [datasetsResponse]);

  // Find the default dataset
  const defaultDataset = useMemo(() => {
    return datasets.find((d) => d.type === 'default');
  }, [datasets]);

  // Set default dataset as initial value if value is null and autoSelectDefault is true
  React.useEffect(() => {
    if (autoSelectDefault && value === null && defaultDataset && !isLoading) {
      onChange(defaultDataset.id);
    }
  }, [autoSelectDefault, value, defaultDataset, isLoading, onChange]);

  const handleCreateDataset = async () => {
    if (!newDatasetName.trim()) return;

    setIsCreating(true);
    try {
      const teamId = ownerTeam === PRIVATE_OWNER ? undefined : ownerTeam;
      const newDataset = await datasetService.create(newDatasetName.trim(), 'import', teamId);

      // Invalidate and refetch datasets
      await queryClient.invalidateQueries({ queryKey: datasetKeys.all });

      // Select the newly created dataset
      onChange(newDataset.id);

      // Reset create mode
      setNewDatasetName('');
      setOwnerTeam(PRIVATE_OWNER);
      setCreateMode(false);

      // Show success toast
      toast.success(`Collection "${newDataset.name}" created successfully`);
    } catch (error) {
      console.error('Failed to create dataset:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create Collection';
      toast.error(errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  const handleSelectChange = (selectedValue: string) => {
    if (selectedValue === '__create_new__') {
      setCreateMode(true);
    } else {
      onChange(selectedValue);
    }
  };

  const selectedDataset = useMemo(() => {
    return datasets.find((d) => d.id === value);
  }, [datasets, value]);

  if (createMode) {
    return (
      <div className={className}>
        {label && <Label className="text-sm font-medium text-gray-700">{label}</Label>}
        <div className="mt-1.5 space-y-2">
          <div className="flex gap-2">
            <Input
              value={newDatasetName}
              onChange={(e) => setNewDatasetName(e.target.value)}
              placeholder="Enter Collection name..."
              disabled={isCreating}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleCreateDataset();
                } else if (e.key === 'Escape') {
                  setCreateMode(false);
                  setNewDatasetName('');
                  setOwnerTeam(PRIVATE_OWNER);
                }
              }}
              autoFocus
            />
            <Button
              onClick={handleCreateDataset}
              disabled={!newDatasetName.trim() || isCreating}
              size="sm"
            >
              {isCreating ? 'Creating...' : 'Create'}
            </Button>
            <Button
              onClick={() => {
                setCreateMode(false);
                setNewDatasetName('');
                setOwnerTeam(PRIVATE_OWNER);
              }}
              variant="outline"
              size="sm"
              disabled={isCreating}
            >
              Cancel
            </Button>
          </div>
          {/* D2: owning team. Only shown when the caller belongs to a team;
              otherwise a new collection is always private to them. */}
          {teams.length > 0 && (
            <div className="space-y-1">
              <Label className="text-xs font-medium text-gray-600">Sharing</Label>
              <Select
                value={ownerTeam}
                onValueChange={setOwnerTeam}
                disabled={isCreating}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={PRIVATE_OWNER}>Private (only you)</SelectItem>
                  {teams.map((team) => (
                    <SelectItem key={team.id} value={team.id}>
                      {`Share with ${team.name}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {helpText && (
            <p className="text-xs text-gray-500">{helpText}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {label && <Label className="text-sm font-medium text-gray-700">{label}</Label>}
      <div className="mt-1.5 space-y-2">
        <Select
          value={value || ''}
          onValueChange={handleSelectChange}
          disabled={isLoading}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder={isLoading ? 'Loading...' : 'Select a Collection'}>
              {selectedDataset ? (
                <div className="flex items-center gap-2">
                  <span>{selectedDataset.name}</span>
                  {selectedDataset.type === 'default' && (
                    <Badge variant="outline" className="text-xs">
                      Default
                    </Badge>
                  )}
                </div>
              ) : (
                'Select a Collection'
              )}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {datasets.map((dataset) => (
              <SelectItem key={dataset.id} value={dataset.id}>
                <div className="flex items-center gap-2 w-full">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate">{dataset.name}</span>
                      {dataset.type === 'default' && (
                        <Badge variant="outline" className="text-xs">
                          Default
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge variant="outline" className="text-xs">
                      {dataset.type}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {dataset.recordCount.toLocaleString()}
                    </Badge>
                  </div>
                </div>
              </SelectItem>
            ))}
            <SelectItem value="__create_new__" className="border-t mt-1 pt-1">
              <div className="flex items-center gap-2 text-blue-600 font-medium">
                <span>+ Create New Collection</span>
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
        {helpText && (
          <p className="text-xs text-gray-500">{helpText}</p>
        )}
      </div>
    </div>
  );
};

export default DatasetSelector;
